import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { getDashboardStats } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Calculator, ShoppingCart, TrendingUp, Euro } from 'lucide-react';
import { format } from 'date-fns';

const STATI_COLORS = {
  creato: 'bg-slate-500',
  inviato: 'bg-blue-500',
  confermato: 'bg-green-500',
  ricevuto: 'bg-primary',
  annullato: 'bg-destructive',
};

const StaffDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="staff-dashboard">
      <div>
        <h1 className="text-3xl font-bold">Ciao, {user?.nome}!</h1>
        <p className="text-muted-foreground mt-1">Ecco il riepilogo delle tue attività</p>
      </div>

      {/* Stats principali */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/20">
                <Calculator className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Chiusure</p>
                <p className="text-2xl font-bold font-mono">{stats?.chiusure_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <ShoppingCart className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Ordini</p>
                <p className="text-2xl font-bold font-mono">{stats?.ordini_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Incassi registrati */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Le Tue Chiusure
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center p-4 bg-blue-500/10 rounded-lg">
              <p className="text-xs text-muted-foreground">Totale Fiscale</p>
              <p className="text-2xl font-bold font-mono text-blue-500">
                €{(stats?.totale_fiscale || 0).toFixed(0)}
              </p>
            </div>
            <div className="text-center p-4 bg-primary/10 rounded-lg">
              <p className="text-xs text-muted-foreground">Totale Completo</p>
              <p className="text-2xl font-bold font-mono text-primary">
                €{(stats?.totale_completo || 0).toFixed(0)}
              </p>
            </div>
          </div>
          
          <div className="text-center p-3 bg-secondary/50 rounded-lg">
            <p className="text-xs text-muted-foreground">Contanti Extra Totali</p>
            <p className={`text-xl font-mono font-semibold ${(stats?.contanti_extra_totale || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              €{(stats?.contanti_extra_totale || 0).toFixed(2)}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Ordini */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-blue-500" />
            I Tuoi Ordini
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-4 bg-blue-500/10 rounded-lg">
            <p className="text-sm text-muted-foreground">Totale Ordinato</p>
            <p className="text-4xl font-bold font-mono text-blue-500">
              €{(stats?.ordini_totale || 0).toFixed(2)}
            </p>
          </div>

          {/* Stati ordini */}
          {stats?.ordini_per_stato && Object.keys(stats.ordini_per_stato).length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.entries(stats.ordini_per_stato).map(([stato, count]) => (
                <Badge key={stato} className={`${STATI_COLORS[stato] || 'bg-slate-500'} text-white`}>
                  {stato}: {count}
                </Badge>
              ))}
            </div>
          )}

          {/* Ultimi ordini */}
          {stats?.ultimi_ordini && stats.ultimi_ordini.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Ultimi ordini:</p>
              {stats.ultimi_ordini.map((ordine, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 bg-secondary/30 rounded">
                  <div>
                    <p className="font-medium text-sm">{ordine.fornitore_nome}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(ordine.created_at), 'dd/MM/yy HH:mm')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold">€{ordine.totale.toFixed(2)}</p>
                    <Badge className={`${STATI_COLORS[ordine.stato]} text-white text-xs`}>
                      {ordine.stato}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ultime chiusure */}
      {stats?.ultime_chiusure && stats.ultime_chiusure.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Ultime Chiusure</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.ultime_chiusure.map((chiusura, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                  <p className="font-mono text-sm">
                    {format(new Date(chiusura.data), 'dd/MM/yyyy')}
                  </p>
                  <div className="text-right">
                    <p className="font-mono font-bold text-primary">
                      €{(chiusura.totale_completo || chiusura.incasso_totale || 0).toFixed(0)}
                    </p>
                    {chiusura.contanti_extra !== undefined && (
                      <p className={`text-xs font-mono ${chiusura.contanti_extra >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        Extra: €{chiusura.contanti_extra.toFixed(0)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StaffDashboard;
